import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Plus, Save, Trash2, TestTube, Shield, Globe, Target, Tag, Cog, Users, Cloud, AlertCircle, Loader2, Edit, Calendar, User, X, RotateCcw, Clock, CheckCircle, XCircle, Building2, Mail } from "lucide-react";
import { ProductEditModal } from "@/components/products/ProductEditModal";
import { TagEditModal } from "@/components/tags/TagEditModal";
import { ColorPalette, COLOR_PALETTE } from "@/components/tags/ColorPalette";
import { useDebounce } from "@/hooks/use-debounce";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/AuthProvider";
import { RequireRole } from "@/components/RequireRole";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useIndustries, useCreateIndustry, useDeleteIndustry } from "@/features/industries";
import { EmailSettings } from "@/components/settings/EmailSettings";
import { 
  useProductCategoriesQuery, 
  useAllProductCategoriesQuery,
  useCreateProductCategoryMutation, 
  useUpdateProductCategoryMutation, 
  useDeleteProductCategoryMutation
} from "@/hooks/useProductCategoriesQuery";
import type { ProductCategory } from "@/services/productCategoriesService";

// Form validation schemas
const productSchema = z.object({
  name: z.string().min(1, "Product name is required"),
  category_id: z.string().min(1, "Please select a category"),
  base_price: z.string()
    .optional()
    .refine((val) => !val || (parseFloat(val) > 0), "Price must be a positive number"),
  description: z.string().optional(),
});

const productCategorySchema = z.object({
  name: z.string().min(1, "Category name is required"),
  description: z.string().optional(),
});

const tagSchema = z.object({
  name: z.string().min(1, "Tag name is required"),
  color: z.string().min(4, "Please select a color"),
});

const industrySchema = z.object({
  name: z.string().min(1, "Industry name is required"),
});

// Products Section Component
const ProductsSection = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);

  const form = useForm<z.infer<typeof productSchema>>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: "",
      category_id: "",
      base_price: "",
      description: "",
    },
  });

  const { data: categories } = useProductCategoriesQuery();

  const { data: products, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select(`
          *,
          product_categories!products_category_id_fkey (
            id,
            name
          )
        `)
        .order("name");
      if (error) throw error;
      return data;
    }
  });

  const createProductMutation = useMutation({
    mutationFn: async (values: z.infer<typeof productSchema>) => {
      const productData = {
        name: values.name,
        category_id: values.category_id,
        description: values.description || null,
        base_price: values.base_price ? parseFloat(values.base_price) : null
      };
      const { data, error } = await supabase.from("products").insert([productData]);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setIsAddingProduct(false);
      form.reset({
        name: "",
        category_id: "",
        base_price: "",
        description: "",
      });
      toast({ title: "Success", description: "Product created successfully" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: "Failed to create product: " + error.message,
        variant: "destructive"
      });
    }
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("products").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({ title: "Success", description: "Product deleted successfully" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: "Failed to delete product: " + error.message,
        variant: "destructive"
      });
    }
  });

  const onSubmit = (values: z.infer<typeof productSchema>) => {
    createProductMutation.mutate(values);
  };

  if (productsError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load products: {productsError.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Products Management</h3>
          <p className="text-sm text-muted-foreground">Manage research tools and products</p>
        </div>
        <Button onClick={() => setIsAddingProduct(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Product
        </Button>
      </div>

      {isAddingProduct && (
        <Card>
          <CardHeader>
            <CardTitle>Add New Product</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Product Name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. Brand U&A, CX Tracker" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="category_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {categories?.map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="base_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Pricing (€)</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="12000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Internal notes about this product" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2">
                  <Button type="submit" disabled={createProductMutation.isPending}>
                    {createProductMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    <Save className="h-4 w-4 mr-2" />
                    Save Product
                  </Button>
                  <Button variant="outline" onClick={() => setIsAddingProduct(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {productsLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[200px]" />
                  <Skeleton className="h-3 w-[300px]" />
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-[80px]" />
                    <Skeleton className="h-6 w-[60px]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          products?.map((product) => (
            <Card key={product.id}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold">{product.name}</h4>
                    <p className="text-sm text-muted-foreground">{product.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline">
                        {product.product_categories?.name || 'No Category'}
                      </Badge>
                      {product.base_price && (
                        <Badge variant="secondary">
                          {new Intl.NumberFormat('el-GR', { 
                            style: 'currency', 
                            currency: 'EUR' 
                          }).format(product.base_price)}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>Created {new Date(product.created_at).toLocaleDateString()}</span>
                      </div>
                      {product.updated_at && new Date(product.updated_at) > new Date(product.created_at) && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>Updated {new Date(product.updated_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setEditingProduct(product)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => deleteProductMutation.mutate(product.id)}
                      disabled={deleteProductMutation.isPending}
                    >
                      {deleteProductMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {editingProduct && (
        <ProductEditModal
          product={editingProduct}
          open={!!editingProduct}
          onOpenChange={(open) => !open && setEditingProduct(null)}
        />
      )}
    </div>
  );
};

// Tags Section Component
const TagsSection = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeCategory, setActiveCategory] = useState("people");
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [editingTag, setEditingTag] = useState<any>(null);
  const [selectedColor, setSelectedColor] = useState(COLOR_PALETTE[0]);

  const tagForm = useForm<z.infer<typeof tagSchema>>({
    resolver: zodResolver(tagSchema),
    defaultValues: {
      name: "",
      color: "#3b82f6",
    },
  });

  const { data: tags, isLoading: tagsLoading, error: tagsError } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tags").select("*").order("name");
      if (error) throw error;
      return data;
    }
  });

  const createTagMutation = useMutation({
    mutationFn: async (values: z.infer<typeof tagSchema>) => {
      const tagData = {
        name: values.name,
        color: values.color,
        category: activeCategory
      };
      const { data, error } = await supabase.from("tags").insert([tagData]);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      setIsAddingTag(false);
      tagForm.reset();
      toast({ title: "Success", description: "Tag created successfully" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: "Failed to create tag: " + error.message,
        variant: "destructive"
      });
    }
  });

  const deleteTagMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("tags").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      toast({ title: "Success", description: "Tag deleted successfully" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: "Failed to delete tag: " + error.message,
        variant: "destructive"
      });
    }
  });

  const onTagSubmit = (values: z.infer<typeof tagSchema>) => {
    createTagMutation.mutate(values);
  };

  const handleColorSelect = (color: string) => {
    setSelectedColor(color);
    tagForm.setValue("color", color);
  };

  const filteredTags = tags?.filter(tag => tag.category === activeCategory) || [];

  if (tagsError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load tags: {tagsError.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Tags Management</h3>
        <p className="text-sm text-muted-foreground">Organize labels for filtering and analytics</p>
      </div>

      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList>
          <TabsTrigger value="people">People Tags</TabsTrigger>
          <TabsTrigger value="proposal">Proposal Tags</TabsTrigger>
          <TabsTrigger value="activity">Activity Tags</TabsTrigger>
        </TabsList>

        <TabsContent value={activeCategory} className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setIsAddingTag(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Tag
            </Button>
          </div>

          {isAddingTag && (
            <Card>
              <CardContent className="pt-6">
                <Form {...tagForm}>
                  <form onSubmit={tagForm.handleSubmit(onTagSubmit)} className="space-y-4">
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <FormField
                          control={tagForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tag Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter tag name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                    
                    <FormField
                      control={tagForm.control}
                      name="color"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Color</FormLabel>
                          <div className="space-y-3">
                            <ColorPalette 
                              selectedColor={selectedColor}
                              onColorSelect={handleColorSelect}
                            />
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">Custom:</span>
                              <FormControl>
                                <Input 
                                  type="color" 
                                  className="w-16 h-8 p-1" 
                                  value={selectedColor}
                                  onChange={(e) => handleColorSelect(e.target.value)}
                                />
                              </FormControl>
                            </div>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" disabled={createTagMutation.isPending}>
                        {createTagMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Save
                      </Button>
                      <Button variant="outline" onClick={() => setIsAddingTag(false)} size="sm">
                        Cancel
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          )}

          <div className="flex flex-wrap gap-2">
            {tagsLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-[80px]" />
              ))
            ) : (
              filteredTags.map((tag) => (
                <div
                  key={tag.id}
                  className="group relative flex items-center gap-2 p-2 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                >
                  <Badge
                    style={{ backgroundColor: tag.color, color: "white" }}
                    className="px-3 py-1"
                  >
                    {tag.name}
                  </Badge>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingTag(tag)}
                      className="h-6 w-6 p-0"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteTagMutation.mutate(tag.id)}
                      disabled={deleteTagMutation.isPending}
                      className="h-6 w-6 p-0 hover:text-destructive"
                    >
                      {deleteTagMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <X className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {editingTag && (
        <TagEditModal
          tag={editingTag}
          open={!!editingTag}
          onOpenChange={(open) => !open && setEditingTag(null)}
        />
      )}
    </div>
  );
};

// Probability Settings Component
const ProbabilitySection = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [originalValues, setOriginalValues] = useState<Record<string, number>>({});
  const [tempValues, setTempValues] = useState<Record<string, number>>({});

  // Default probability values by status
  const DEFAULT_PROBABILITIES = {
    'draft': 0,
    'sent': 25,
    'pending': 50,
    'accepted': 100,
    'rejected': 0
  };

  const { data: probabilities, isLoading: probabilitiesLoading, error: probabilitiesError } = useQuery({
    queryKey: ["forecast_probabilities"],
    queryFn: async () => {
      const { data, error } = await supabase.from("forecast_probabilities").select("*").order("status");
      if (error) throw error;
      
      // Initialize original and temp values
      const original: Record<string, number> = {};
      const temp: Record<string, number> = {};
      data.forEach(prob => {
        original[prob.id] = prob.probability;
        temp[prob.id] = prob.probability;
      });
      setOriginalValues(original);
      setTempValues(temp);
      
      return data;
    }
  });

  const debouncedTempValues = useDebounce(tempValues, 1000);

  const updateProbabilityMutation = useMutation({
    mutationFn: async ({ id, probability }: { id: string; probability: number }) => {
      const { data, error } = await supabase
        .from("forecast_probabilities")
        .update({ probability })
        .eq("id", id);
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["forecast_probabilities"] });
      setOriginalValues(prev => ({ ...prev, [variables.id]: variables.probability }));
      toast({ title: "Success", description: "Probability updated successfully" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: "Failed to update probability: " + error.message,
        variant: "destructive"
      });
    }
  });

  const resetToDefaultsMutation = useMutation({
    mutationFn: async () => {
      const updates = probabilities?.map(prob => ({
        id: prob.id,
        probability: DEFAULT_PROBABILITIES[prob.status as keyof typeof DEFAULT_PROBABILITIES] || 0
      })) || [];

      const promises = updates.map(update => 
        supabase
          .from("forecast_probabilities")
          .update({ probability: update.probability })
          .eq("id", update.id)
      );

      const results = await Promise.all(promises);
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        throw new Error(`Failed to reset ${errors.length} probabilities`);
      }
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forecast_probabilities"] });
      toast({ title: "Success", description: "All probabilities reset to defaults" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: "Failed to reset probabilities: " + error.message,
        variant: "destructive"
      });
    }
  });

  // Effect to handle debounced updates
  React.useEffect(() => {
    if (probabilities && Object.keys(debouncedTempValues).length > 0) {
      Object.entries(debouncedTempValues).forEach(([id, value]) => {
        if (originalValues[id] !== undefined && originalValues[id] !== value) {
          updateProbabilityMutation.mutate({ id, probability: value });
        }
      });
    }
  }, [debouncedTempValues]);

  const handleValueChange = (id: string, newValue: number) => {
    setTempValues(prev => ({ ...prev, [id]: newValue }));
  };

  const formatLastUpdated = (date: string) => {
    return new Date(date).toLocaleString('el-GR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (probabilitiesError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load probabilities: {probabilitiesError.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold">Probability Settings</h3>
          <p className="text-sm text-muted-foreground">Configure forecasting probabilities by proposal status</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => resetToDefaultsMutation.mutate()}
          disabled={resetToDefaultsMutation.isPending}
          className="flex items-center gap-2"
        >
          {resetToDefaultsMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RotateCcw className="h-4 w-4" />
          )}
          Reset to Defaults
        </Button>
      </div>

      <div className="space-y-4">
        {probabilitiesLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-5 w-[120px]" />
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-9 w-20" />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          probabilities?.map((prob) => {
            const hasChanges = tempValues[prob.id] !== originalValues[prob.id];
            return (
              <Card key={prob.id} className={hasChanges ? "border-primary/50 bg-primary/5" : ""}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <h4 className="font-medium capitalize flex items-center gap-2">
                        {prob.status.replace("_", " ")}
                        {hasChanges && (
                          <Badge variant="secondary" className="text-xs">
                            Unsaved
                          </Badge>
                        )}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>Last updated: {formatLastUpdated(prob.updated_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={tempValues[prob.id] ?? prob.probability}
                          onChange={(e) => {
                            const newValue = parseInt(e.target.value);
                            if (newValue >= 0 && newValue <= 100) {
                              handleValueChange(prob.id, newValue);
                            }
                          }}
                          className={`w-20 ${hasChanges ? "border-primary" : ""}`}
                          disabled={updateProbabilityMutation.isPending}
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                      {updateProbabilityMutation.isPending && updateProbabilityMutation.variables?.id === prob.id && (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <div className="text-xs text-muted-foreground space-y-1">
        <p><strong>Default values:</strong> Draft: 0%, Sent: 25%, Pending: 50%, Accepted: 100%, Rejected: 0%</p>
        <p><strong>Auto-save:</strong> Changes are automatically saved 1 second after you stop typing</p>
      </div>
    </div>
  );
};

// General Settings Component
const GeneralSettingsSection = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const { data: settings, isLoading: settingsLoading, error: settingsError } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("settings").select("*");
      if (error) throw error;
      return data;
    }
  });

  const updateSettingMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const { data, error } = await supabase
        .from("settings")
        .update({ value })
        .eq("key", key);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      toast({ title: "Success", description: "Setting updated successfully" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: "Failed to update setting: " + error.message,
        variant: "destructive"
      });
    }
  });

  // Simplified helper functions
  const getSettingValue = (key: string, defaultValue: any = null) => {
    // Handle case when settings is undefined or not an array
    if (!settings || !Array.isArray(settings)) {
      return defaultValue;
    }
    
    const setting = settings.find(s => s.key === key);
    if (!setting) return defaultValue;
    
    let value = setting.value;
    
    // Handle string values that might be JSON-wrapped
    if (typeof value === 'string' && value.startsWith('"') && value.endsWith('"')) {
      value = value.slice(1, -1);
    }
    
    return value ?? defaultValue;
  };

  const getSettingAsString = (key: string, defaultValue: string = ""): string => {
    return String(getSettingValue(key, defaultValue));
  };

  const getSettingAsNumber = (key: string, defaultValue: number = 0): number => {
    const value = getSettingValue(key, defaultValue);
    const parsed = Number(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  const getSettingAsBoolean = (key: string, defaultValue: boolean = false): boolean => {
    const value = getSettingValue(key, defaultValue);
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return defaultValue;
  };

  // Validation functions
  const validateFolderStructure = (structure: string): { isValid: boolean; error?: string } => {
    if (!structure.trim()) return { isValid: true };
    
    // Check for valid placeholder format
    const placeholderRegex = /\{[^}]+\}/g;
    const placeholders = structure.match(placeholderRegex) || [];
    
    const validPlaceholders = [
      '{Client Name}', '{Project Name}', '{Proposal Name}', 
      '{Date}', '{User Name}', '{Invoice Number}'
    ];
    
    for (const placeholder of placeholders) {
      if (!validPlaceholders.includes(placeholder)) {
        return { 
          isValid: false, 
          error: `Invalid placeholder: ${placeholder}. Valid options: ${validPlaceholders.join(', ')}` 
        };
      }
    }
    
    // Check for valid path format
    if (structure.includes('//') || structure.endsWith('/')) {
      return { 
        isValid: false, 
        error: "Invalid path format. Avoid double slashes or trailing slashes." 
      };
    }
    
    return { isValid: true };
  };

  const testGoogleDriveConnection = async () => {
    setIsTestingConnection(true);
    setConnectionStatus('idle');
    
    try {
      const { data, error } = await supabase.functions.invoke('test-google-drive');
      
      if (error) throw error;
      
      if (data.success) {
        setConnectionStatus('success');
        toast({ 
          title: "Connection Successful", 
          description: `Connected to Google Drive as: ${data.user}` 
        });
      } else {
        setConnectionStatus('error');
        toast({ 
          title: "Connection Failed", 
          description: data.error,
          variant: "destructive"
        });
      }
    } catch (error: any) {
      setConnectionStatus('error');
      toast({ 
        title: "Connection Error", 
        description: error.message || "Failed to test Google Drive connection",
        variant: "destructive"
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleFolderStructureChange = (value: string) => {
    const validation = validateFolderStructure(value);
    
    if (!validation.isValid) {
      toast({
        title: "Invalid Format",
        description: validation.error,
        variant: "destructive"
      });
      return;
    }
    
    updateSettingMutation.mutate({
      key: "google_drive_folder_structure",
      value: value
    });
  };

  if (settingsError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load settings: {settingsError.message}
        </AlertDescription>
      </Alert>
    );
  }

  if (settingsLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold">General Settings</h3>
          <p className="text-sm text-muted-foreground">Configure system-wide settings</p>
        </div>
        <div className="grid gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-[150px]" />
                <Skeleton className="h-4 w-[250px]" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">General Settings</h3>
        <p className="text-sm text-muted-foreground">Configure system-wide settings</p>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recurring Logic</CardTitle>
            <CardDescription>Default settings for subscription management</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Default Duration (months)</Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={getSettingAsNumber("recurring_default_duration", 12)}
                  onChange={(e) => updateSettingMutation.mutate({
                    key: "recurring_default_duration",
                    value: parseInt(e.target.value)
                  })}
                />
              </div>
              <div>
                <Label>Invoice Schedule (day of month)</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={getSettingAsNumber("recurring_invoice_schedule", 1)}
                  onChange={(e) => updateSettingMutation.mutate({
                    key: "recurring_invoice_schedule",
                    value: parseInt(e.target.value)
                  })}
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={getSettingAsBoolean("auto_generate_recurring", false)}
                onCheckedChange={(checked) => updateSettingMutation.mutate({
                  key: "auto_generate_recurring",
                  value: checked
                })}
              />
              <Label>Auto-generate recurring invoices</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Localization</CardTitle>
            <CardDescription>Currency and country settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Default Currency</Label>
              <Select
                value={getSettingAsString("default_currency", "EUR")}
                onValueChange={(value) => updateSettingMutation.mutate({
                  key: "default_currency",
                  value: value
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="RON">RON - Romanian Leu</SelectItem>
                  <SelectItem value="PLN">PLN - Polish Złoty</SelectItem>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Google Drive Integration
              {connectionStatus === 'success' && <CheckCircle className="h-5 w-5 text-green-500" />}
              {connectionStatus === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
            </CardTitle>
            <CardDescription>Configure file storage settings and test API connection</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Folder Structure Template</Label>
              <Input
                value={getSettingAsString("google_drive_folder_structure", "")}
                onChange={(e) => handleFolderStructureChange(e.target.value)}
                placeholder="/Clients/{Client Name}/Projects/{Project Name}/Files"
              />
              <div className="mt-2 text-xs text-muted-foreground">
                <p><strong>Valid placeholders:</strong></p>
                <p>{`{Client Name}, {Project Name}, {Proposal Name}, {Date}, {User Name}, {Invoice Number}`}</p>
                <p><strong>Example:</strong> /Clients/{`{Client Name}`}/Projects/{`{Project Name}`}/Documents</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button 
                onClick={testGoogleDriveConnection}
                disabled={isTestingConnection}
                className="flex items-center gap-2"
                variant={connectionStatus === 'success' ? 'default' : connectionStatus === 'error' ? 'destructive' : 'outline'}
              >
                {isTestingConnection ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : connectionStatus === 'success' ? (
                  <CheckCircle className="h-4 w-4" />
                ) : connectionStatus === 'error' ? (
                  <XCircle className="h-4 w-4" />
                ) : (
                  <TestTube className="h-4 w-4" />
                )}
                {isTestingConnection ? 'Testing...' : 'Test Connection'}
              </Button>
              
              {connectionStatus !== 'idle' && (
                <span className={`text-sm ${connectionStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {connectionStatus === 'success' ? 'Connection successful' : 'Connection failed'}
                </span>
              )}
            </div>
            
            <Alert>
              <Cloud className="h-4 w-4" />
              <AlertDescription>
                <strong>Setup required:</strong> Configure your Google Drive API key in Supabase secrets as 'GOOGLE_DRIVE_API_KEY'.
                <br />
                <a 
                  href="https://console.cloud.google.com/apis/credentials" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Create API credentials →
                </a>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Industries Section Component
const IndustriesSection = () => {
  const { toast } = useToast();
  const { data: industries = [], isLoading } = useIndustries();
  const createIndustryMutation = useCreateIndustry();
  const deleteIndustryMutation = useDeleteIndustry();
  const [isAddingIndustry, setIsAddingIndustry] = useState(false);

  const industryForm = useForm<z.infer<typeof industrySchema>>({
    resolver: zodResolver(industrySchema),
    defaultValues: {
      name: "",
    },
  });

  const onSubmit = (values: z.infer<typeof industrySchema>) => {
    if (industries.some(industry => industry.name.toLowerCase() === values.name.toLowerCase())) {
      toast({ 
        title: "Error", 
        description: "Industry already exists",
        variant: "destructive"
      });
      return;
    }

    createIndustryMutation.mutate(values, {
      onSuccess: () => {
        setIsAddingIndustry(false);
        industryForm.reset();
      }
    });
  };

  const handleRemoveIndustry = (id: string) => {
    deleteIndustryMutation.mutate(id);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Industries Management</h3>
          <p className="text-sm text-muted-foreground">Manage industry classifications for clients</p>
        </div>
        <Button onClick={() => setIsAddingIndustry(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Industry
        </Button>
      </div>

      {isAddingIndustry && (
        <Card>
          <CardHeader>
            <CardTitle>Add New Industry</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...industryForm}>
              <form onSubmit={industryForm.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={industryForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Industry Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Technology, Healthcare, Finance" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2">
                  <Button type="submit" disabled={createIndustryMutation.isPending}>
                    {createIndustryMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Industry
                  </Button>
                  <Button variant="outline" onClick={() => setIsAddingIndustry(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {isLoading ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <Skeleton className="h-4 w-32 mx-auto" />
            </CardContent>
          </Card>
        ) : (
          <>
            {industries.map((industry) => (
              <Card key={industry.id}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">{industry.name}</h4>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleRemoveIndustry(industry.id)}
                      className="hover:text-destructive"
                      disabled={deleteIndustryMutation.isPending}
                    >
                      {deleteIndustryMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {industries.length === 0 && (
              <Card>
                <CardContent className="pt-6 text-center text-muted-foreground">
                  No industries added yet. Click "Add Industry" to get started.
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Product Categories Section Component
const ProductCategoriesSection = () => {
  const { toast } = useToast();
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);

  const form = useForm<z.infer<typeof productCategorySchema>>({
    resolver: zodResolver(productCategorySchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const { data: categories, isLoading: categoriesLoading } = useAllProductCategoriesQuery();
  const createCategoryMutation = useCreateProductCategoryMutation();
  const updateCategoryMutation = useUpdateProductCategoryMutation();
  const deleteCategoryMutation = useDeleteProductCategoryMutation();

  const handleSubmit = async (values: z.infer<typeof productCategorySchema>) => {
    if (editingCategory) {
      updateCategoryMutation.mutate({
        id: editingCategory.id,
        ...values
      }, {
        onSuccess: () => {
          setEditingCategory(null);
          form.reset();
          toast({ title: "Success", description: "Category updated successfully" });
        },
        onError: (error: any) => {
          toast({ 
            title: "Error", 
            description: "Failed to update category: " + error.message,
            variant: "destructive"
          });
        }
      });
    } else {
      createCategoryMutation.mutate(values, {
        onSuccess: () => {
          setIsAddingCategory(false);
          form.reset();
          toast({ title: "Success", description: "Category created successfully" });
        },
        onError: (error: any) => {
          toast({ 
            title: "Error", 
            description: "Failed to create category: " + error.message,
            variant: "destructive"
          });
        }
      });
    }
  };

  const handleEdit = (category: ProductCategory) => {
    setEditingCategory(category);
    setIsAddingCategory(true);
    form.reset({
      name: category.name,
      description: category.description || "",
    });
  };

  const handleDelete = (categoryId: string) => {
    if (confirm("Are you sure you want to delete this category?")) {
      deleteCategoryMutation.mutate(categoryId, {
        onSuccess: () => {
          toast({ title: "Success", description: "Category deleted successfully" });
        },
        onError: (error: any) => {
          toast({ 
            title: "Error", 
            description: "Failed to delete category: " + error.message,
            variant: "destructive"
          });
        }
      });
    }
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setIsAddingCategory(false);
    form.reset();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Product Categories</CardTitle>
            <CardDescription>Manage product categories for better organization</CardDescription>
          </div>
          {!isAddingCategory && (
            <Button onClick={() => setIsAddingCategory(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isAddingCategory && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {editingCategory ? "Edit Category" : "Add New Category"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Category name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description (Optional)</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Category description" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex gap-2">
                    <Button 
                      type="submit" 
                      disabled={createCategoryMutation.isPending || updateCategoryMutation.isPending}
                    >
                      {(createCategoryMutation.isPending || updateCategoryMutation.isPending) && (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      )}
                      {editingCategory ? "Update" : "Create"}
                    </Button>
                    <Button type="button" variant="outline" onClick={cancelEdit}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        )}

        <div className="space-y-2">
          {categoriesLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            categories?.map((category) => (
              <Card key={category.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{category.name}</h3>
                        {!category.is_active && (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </div>
                      {category.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {category.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(category)}
                        disabled={createCategoryMutation.isPending || updateCategoryMutation.isPending}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(category.id)}
                        disabled={deleteCategoryMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function Settings() {
  return (
    <RequireRole role="admin">
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>

        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="products" className="flex items-center gap-2">
              <TestTube className="h-4 w-4" />
              Products
            </TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="tags" className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Tags
            </TabsTrigger>
            <TabsTrigger value="probabilities" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Probabilities
            </TabsTrigger>
            <TabsTrigger value="industries" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Industries
            </TabsTrigger>
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Cog className="h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            <ProductsSection />
          </TabsContent>

          <TabsContent value="categories">
            <ProductCategoriesSection />
          </TabsContent>

          <TabsContent value="tags">
            <TagsSection />
          </TabsContent>

          <TabsContent value="probabilities">
            <ProbabilitySection />
          </TabsContent>

          <TabsContent value="industries">
            <IndustriesSection />
          </TabsContent>

          <TabsContent value="general">
            <GeneralSettingsSection />
          </TabsContent>
          <TabsContent value="email">
            <EmailSettings />
          </TabsContent>
        </Tabs>
      </div>
    </RequireRole>
  );
}